import React, { useState, useEffect } from 'react';
import EmployeeProcessService from "../services/EmployeeProcessService";
import { useLoading } from '../Utils/LoadingContext';
import { toast } from 'react-toastify';

const WeekOffAllocation = () => {
    const empcode = localStorage.getItem("empcode");
    const { setLoading } = useLoading();

    /* ========================================
       STATES
    ======================================== */

    const [plantList, setPlantList] = useState([]);
    const [departmentList, setDepartmentList] = useState([]);
    const [weekOffList, setWeekOffList] = useState([]);

    const [filters, setFilters] = useState({
        PlantName: '',
        DepartmentName: '',
        EmployeeCode: ''
    });

    const [searchCode, setSearchCode] = useState('');

    /* Employee WeekOff Allocation */

    const [employees, setEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedWeekOff, setSelectedWeekOff] = useState('');

    /* Toggle */

    const [showEmployeeTable, setShowEmployeeTable] = useState(true);
    const [showOverrideTable, setShowOverrideTable] = useState(true);

    /* Special Override */

    const [overrideForm, setOverrideForm] = useState({
        EmpCode: '',
        OffDate: '',
        IsWeekOff: '1',
        Reason: ''
    });

    const [overrideData, setOverrideData] = useState([]);

    /* ========================================
       INITIAL LOAD
    ======================================== */

    useEffect(() => {
        loadPlants();
    }, []);

    const loadPlants = async () => {
        try {
            const res = await EmployeeProcessService.getPlant(empcode);

            if (res?.data?.statusCode === 200) {
                setPlantList(res.data.result);
            }
        } catch (error) {
            console.error(error);
        }
    };

    /* ========================================
       PLANT CHANGE
    ======================================== */

    const handlePlantChange = async (e) => {
        const plantId = e.target.value;

        setFilters(prev => ({
            ...prev,
            PlantName: plantId,
            DepartmentName: ''
        }));

        setEmployees([]);
        setSelectedEmployees([]);
        setSelectAll(false);
        setSelectedWeekOff('');
        setDepartmentList([]);
        setWeekOffList([]);
        setOverrideData([]);

        if (!plantId) return;

        try {
            /* Department */

            const deptRes = await EmployeeProcessService.getDepartment(
                empcode,
                plantId
            );

            if (deptRes?.data?.statusCode === 200) {
                setDepartmentList(deptRes.data.result);
            }

            /* WeekOff */

            const weekRes = await EmployeeProcessService.getWeekOff(
                plantId
            );

            if (weekRes?.data?.statusCode === 200) {
                setWeekOffList(weekRes.data.result);
            }

            /* Override */

            loadOverrideData(plantId);

        } catch (error) {
            console.error(error);
        }
    };

    /* ========================================
       LOAD OVERRIDE DATA
    ======================================== */

    const loadOverrideData = async (plantId) => {
        try {
            const res = await EmployeeProcessService.getWeekOffOverride({
                PlantName: plantId
            });

            if (res?.data?.statusCode === 200) {
                setOverrideData(res.data.result);
            }
        } catch (error) {
            console.error(error);
        }
    };

    /* ========================================
       DEPARTMENT CHANGE
    ======================================== */

    const handleDepartmentChange = (e) => {
        const deptId = e.target.value;

        setFilters(prev => ({
            ...prev,
            DepartmentName: deptId
        }));
    };

    /* ========================================
       LOAD EMPLOYEES
    ======================================== */

    const handleLoadEmployees = async () => {
        if (!filters.PlantName) {
            toast.error("Please select Plant");
            return;
        }

        try {
            setLoading(true);

            const payload = {
                ...filters,
                EmployeeCode: searchCode || ''
            };

            const res = await EmployeeProcessService.getEmployeeWeekOff(payload);

            if (res?.data?.statusCode === 200) {
                setEmployees(res.data.result || []);
                setSelectedEmployees([]);
                setSelectAll(false);
                setShowEmployeeTable(true);
            } else {
                setEmployees([]);
                toast.error("No employees found");
            }

        } catch (error) {
            console.error(error);
            toast.error("Error loading employees");
        } finally {
            setLoading(false);
        }
    };

    /* ========================================
       EMPLOYEE SELECTION
    ======================================== */

    const handleSelectEmployee = (employeeCode) => {
        if (selectedEmployees.includes(employeeCode)) {
            setSelectedEmployees(
                selectedEmployees.filter(x => x !== employeeCode)
            );
        } else {
            setSelectedEmployees([
                ...selectedEmployees,
                employeeCode
            ]);
        }
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedEmployees([]);
        } else {
            setSelectedEmployees(
                employees.map(emp => emp.empCode)
            );
        }

        setSelectAll(!selectAll);
    };

    /* ========================================
       ASSIGN NORMAL WEEKOFF
    ======================================== */

    const handleSubmit = async () => {
        if (!selectedWeekOff) {
            toast.error("Please select WeekOff");
            return;
        }

        if (selectedEmployees.length === 0) {
            toast.error("Please select Employees");
            return;
        }

        try {
            setLoading(true);

            const payload = {
                EmpCode: selectedEmployees.join(','),
                WeekOffID: selectedWeekOff
            };

            const res = await EmployeeProcessService.setEmployeeWeekOff(payload);

            if (res?.data?.statusCode === 200) {
                toast.success("WeekOff assigned successfully");

                setSelectedEmployees([]);
                setSelectAll(false);

                handleLoadEmployees();
            } else {
                toast.error("Assignment failed");
            }

        } catch (error) {
            console.error(error);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    /* ========================================
       SAVE OVERRIDE
    ======================================== */

    const handleOverrideSave = async () => {
        if (!filters.PlantName) {
            toast.error("Please select Plant");
            return;
        }

        if (!overrideForm.OffDate) {
            toast.error("Please select Special Date");
            return;
        }

        if (!overrideForm.Reason) {
            toast.error("Please enter Reason");
            return;
        }

        try {
            setLoading(true);

            const payload = {
                Action: 'WeekOffOverrideSave',
                PlantID: filters.PlantName,
                EmpCode: overrideForm.EmpCode || '',
                OffDate: overrideForm.OffDate,
                IsWeekOff: overrideForm.IsWeekOff,
                Reason: overrideForm.Reason
            };

            const res = await EmployeeProcessService.saveWeekOffOverride(payload);

            if (res?.data?.statusCode === 200) {
                toast.success("Override saved successfully");

                setOverrideForm({
                    Action:'',
                    EmpCode: '',
                    OffDate: '',
                    IsWeekOff: '1',
                    Reason: ''
                });

                loadOverrideData(filters.PlantName);
            } else {
                toast.error("Save failed");
            }

        } catch (error) {
            console.error(error);
            toast.error("Save error");
        } finally {
            setLoading(false);
        }
    };

    /* ========================================
       REMOVE OVERRIDE
    ======================================== */

    const handleRemoveOverride = async (id) => {
        try {
            const res = await EmployeeProcessService.saveWeekOffOverride({
                Action: 'WeekOffOverrideUpdate',
                ID: id
            });

            if (res?.data?.statusCode === 200) {
                toast.success("Override removed successfully");
                loadOverrideData(filters.PlantName);
            }

        } catch (error) {
            console.error(error);
            toast.error("Remove failed");
        }
    };

    /* ========================================
       UI
    ======================================== */

    return (
        <div className="container mt-3">

            <h4 className="text-primary mb-4">
                WeekOff Allocation + Special Override
            </h4>

            {/* ========================================
               FILTER SECTION
            ======================================== */}

            <div className="row mb-4">

                <div className="col-md-3">
                    <label>Plant</label>
                    <select
                        className="form-select"
                        value={filters.PlantName}
                        onChange={handlePlantChange}
                    >
                        <option value="">-- Select Plant --</option>

                        {plantList.map(item => (
                            <option
                                key={item.id}
                                value={item.id}
                            >
                                {item.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="col-md-3">
                    <label>Department</label>
                    <select
                        className="form-select"
                        value={filters.DepartmentName}
                        onChange={handleDepartmentChange}
                    >
                        <option value="">-- Select Department --</option>

                        {departmentList.map(item => (
                            <option
                                key={item.id}
                                value={item.id}
                            >
                                {item.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="col-md-3">
                    <label>Employee Code</label>
                    <input
                        type="text"
                        className="form-control"
                        value={searchCode}
                        onChange={(e) => setSearchCode(e.target.value)}
                        placeholder="Comma separated Employee Code"
                    />
                </div>

                <div className="col-md-2 d-flex align-items-end">
                    <button
                        className="btn btn-primary"
                        onClick={handleLoadEmployees}
                    >
                        Load Employees
                    </button>
                </div>
            </div>

            {/* ========================================
               EMPLOYEE WEEKOFF TABLE
            ======================================== */}

            {employees.length > 0 && (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="text-primary mb-0">
                            Employee WeekOff Allocation
                        </h5>

                        <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                                setShowEmployeeTable(!showEmployeeTable)
                            }
                        >
                            {showEmployeeTable ? "Hide" : "Show"}
                        </button>
                    </div>

                    {showEmployeeTable && (
                        <div
                            style={{
                                maxHeight: "350px",
                                minHeight: "250px",
                                overflowY: "auto",
                                overflowX: "auto",
                                border: "1px solid #ddd",
                                borderRadius: "8px"
                            }}
                        >
                            <table className="table table-bordered table-sm mb-0">
                                <thead
                                    style={{
                                        position: "sticky",
                                        top: 0,
                                        background: "#fff",
                                        zIndex: 10
                                    }}
                                >
                                    <tr>
                                        <th>
                                            <input
                                                type="checkbox"
                                                checked={selectAll}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                        <th>Emp Code</th>
                                        <th>Name</th>
                                        <th>Department</th>
                                        <th>Current WeekOff</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {employees.map(emp => (
                                        <tr key={emp.empCode}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedEmployees.includes(emp.empCode)}
                                                    onChange={() =>
                                                        handleSelectEmployee(emp.empCode)
                                                    }
                                                />
                                            </td>

                                            <td>{emp.empCode}</td>
                                            <td>{emp.empName}</td>
                                            <td>{emp.department}</td>
                                            <td>{emp.weekOffName}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="row mt-4 mb-5">

                        <div className="col-md-4">
                            <label>Select WeekOff</label>
                            <select
                                className="form-select"
                                value={selectedWeekOff}
                                onChange={(e) =>
                                    setSelectedWeekOff(e.target.value)
                                }
                            >
                                <option value="">-- Select WeekOff --</option>

                                {weekOffList.map(item => (
                                    <option
                                        key={item.id}
                                        value={item.id}
                                    >
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-2 d-flex align-items-end">
                            <button
                                className="btn btn-success"
                                onClick={handleSubmit}
                            >
                                Assign WeekOff
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* ========================================
               SPECIAL OVERRIDE SECTION
            ======================================== */}

            <div className="mt-4">

                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="text-success mb-0">
                        Special Date WeekOff Override
                    </h5>

                    <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() =>
                            setShowOverrideTable(!showOverrideTable)
                        }
                    >
                        {showOverrideTable ? "Hide" : "Show"}
                    </button>
                </div>

                {showOverrideTable && (
                    <>
                        {/* FORM */}

                        <div className="row mb-3">

                            <div className="col-md-3">
                                <label>Employee Code</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={overrideForm.EmpCode}
                                    onChange={(e) =>
                                        setOverrideForm({
                                            ...overrideForm,
                                            EmpCode: e.target.value
                                        })
                                    }
                                    placeholder="Blank = Plant Wise"
                                />
                            </div>

                            <div className="col-md-2">
                                <label>Special Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={overrideForm.OffDate}
                                    onChange={(e) =>
                                        setOverrideForm({
                                            ...overrideForm,
                                            OffDate: e.target.value
                                        })
                                    }
                                />
                            </div>

                            <div className="col-md-2">
                                <label>Type</label>
                                <select
                                    className="form-select"
                                    value={overrideForm.IsWeekOff}
                                    onChange={(e) =>
                                        setOverrideForm({
                                            ...overrideForm,
                                            IsWeekOff: e.target.value
                                        })
                                    }
                                >
                                    <option value="1">WeekOff</option>
                                    <option value="0">Working Day</option>
                                </select>
                            </div>

                            <div className="col-md-3">
                                <label>Reason</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={overrideForm.Reason}
                                    onChange={(e) =>
                                        setOverrideForm({
                                            ...overrideForm,
                                            Reason: e.target.value
                                        })
                                    }
                                />
                            </div>

                            <div className="col-md-2 d-flex align-items-end">
                                <button
                                    className="btn btn-warning"
                                    onClick={handleOverrideSave}
                                >
                                    Save Override
                                </button>
                            </div>
                        </div>

                        {/* TABLE */}

                        <div
                            style={{
                                maxHeight: "300px",
                                minHeight: "200px",
                                overflowY: "auto",
                                overflowX: "auto",
                                border: "1px solid #ddd",
                                borderRadius: "8px"
                            }}
                        >
                            <table className="table table-bordered table-sm mb-0">
                                <thead
                                    style={{
                                        position: "sticky",
                                        top: 0,
                                        background: "#fff",
                                        zIndex: 10
                                    }}
                                >
                                    <tr>
                                        <th>Employee</th>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Reason</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {overrideData.map(item => (
                                        <tr key={item.id}>
                                            <td>
                                                {item.empCode || "Plant Wise"}
                                            </td>
                                            <td>{item.offDate}</td>
                                            <td>
                                                {item.isWeekOff}
                                                   
                                            </td>
                                            <td>{item.reason}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() =>
                                                        handleRemoveOverride(item.id)
                                                    }
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

        </div>
    );
};

export default WeekOffAllocation;